/**
 * AUTHENTICA: <authentica-announcements> — 2026-10-06
 *
 * Cross-fades the messages of sections/authentica-announcement-bar.liquid one at a time.
 * The fade itself is CSS (.is-active); this only decides which message is active and when.
 *
 * Pauses while:
 *   - hovered (mouse) or focused (keyboard)
 *   - tapped (touch). A tap pauses; on a linked message that first tap only pauses, and a
 *     second tap follows the link. Tapping a paused bar off the link resumes it.
 *   - the visitor prefers reduced motion — then it never rotates, the first message stays
 *   - the tab is hidden, or the theme editor is open
 */
if (!customElements.get('authentica-announcements')) {
  class AuthenticaAnnouncements extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.authentica-announcements__item'));
      if (this.items.length < 2) return;

      this.index = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active')));
      this.interval = (parseInt(this.dataset.interval, 10) || 5) * 1000;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.hovered = false;
      this.focused = false;
      this.tapped = false;
      this.pointerType = 'mouse';

      this.restart = this.restart.bind(this);
      this.onVisibility = () => this.restart();

      this.addEventListener('mouseenter', () => { this.hovered = true; this.restart(); });
      this.addEventListener('mouseleave', () => { this.hovered = false; this.restart(); });
      this.addEventListener('focusin', () => { this.focused = true; this.restart(); });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) { this.focused = false; this.restart(); }
      });
      this.addEventListener('pointerdown', (event) => { this.pointerType = event.pointerType; });
      this.addEventListener('click', (event) => this.onClick(event));

      this.reducedMotion.addEventListener?.('change', this.restart);
      document.addEventListener('visibilitychange', this.onVisibility);

      // Theme editor: show the message whose block is selected in the sidebar.
      this.addEventListener('shopify:block:select', (event) => {
        const i = this.items.indexOf(event.target);
        if (i > -1) this.show(i);
      });

      this.restart();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.reducedMotion?.removeEventListener?.('change', this.restart);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    onClick(event) {
      const nav = event.target.closest('.authentica-announcements__nav');
      if (nav) {
        this.show(this.index + parseInt(nav.dataset.step, 10));
        this.restart();
        return;
      }
      if (this.pointerType !== 'touch') return;

      const link = event.target.closest('a');
      if (!this.tapped) {
        this.tapped = true;
        if (link) event.preventDefault();
      } else if (!link) {
        this.tapped = false;
      }
      this.restart();
    }

    get paused() {
      return this.hovered || this.focused || this.tapped || this.reducedMotion.matches ||
        document.hidden || Boolean(window.Shopify && Shopify.designMode);
    }

    restart() {
      clearTimeout(this.timer);
      if (this.paused) return;
      this.timer = setTimeout(() => {
        this.show(this.index + 1);
        this.restart();
      }, this.interval);
    }

    show(i) {
      const count = this.items.length;
      const next = ((i % count) + count) % count;
      if (next === this.index) return;
      const current = this.items[this.index];
      const incoming = this.items[next];
      current.classList.remove('is-active');
      current.setAttribute('aria-hidden', 'true');
      current.inert = true;
      incoming.classList.add('is-active');
      incoming.removeAttribute('aria-hidden');
      incoming.inert = false;
      this.index = next;
    }
  }

  customElements.define('authentica-announcements', AuthenticaAnnouncements);
}
